import moment from 'moment';
import { flattenTreeData, nullSafeGet, nullSafeGetOrElse } from './DataAccessUtils';
import { DATE_FORMAT, DATE_ONLY_FORMAT, TIME_ONLY_FORMAT } from './DataFormatterUtils';
import { WO_STEP_NAMES } from './DataConstants';
import { getDefaultFileListForImages } from './FileAccessUtils';

export const STEP_INPUT_TYPE_MAP = {
	DATE_TIME: 'dateTime',
	DATE_ONLY: 'dateOnly',
	TIME_RANGE: 'timeRange',
	TIME_ONLY: 'timeOnly',
	NUMBER: 'number',
	GROUP: 'group',
	REPEATABLE_GROUP: 'repeatableGroup',
	TEXT: 'text',
	IMAGE: 'image',
	PDF: 'pdf',
	ANY_FILE: 'anyFile',
	MULTIPLE_CHOICE: 'multipleChoice',
};

export const StepInputTypes = [
	{
		value: STEP_INPUT_TYPE_MAP.DATE_TIME,
		label: 'Date Time',
	},
	{
		value: STEP_INPUT_TYPE_MAP.DATE_ONLY,
		label: 'Date Only',
	},
	{
		value: STEP_INPUT_TYPE_MAP.TIME_RANGE,
		label: 'Time Range',
	},
	{
		value: STEP_INPUT_TYPE_MAP.TIME_ONLY,
		label: 'Time Only',
	},
	{
		value: STEP_INPUT_TYPE_MAP.MULTIPLE_CHOICE,
		label: 'Multiple Choice',
	},
	{
		value: STEP_INPUT_TYPE_MAP.GROUP,
		label: 'Group',
	},
	{
		value: STEP_INPUT_TYPE_MAP.REPEATABLE_GROUP,
		label: 'Repeatable Group',
	},
	{
		value: STEP_INPUT_TYPE_MAP.NUMBER,
		label: 'Number',
	},
	{
		value: STEP_INPUT_TYPE_MAP.TEXT,
		label: 'Text',
	},
	{
		value: STEP_INPUT_TYPE_MAP.IMAGE,
		label: 'Image',
	},
	{
		value: STEP_INPUT_TYPE_MAP.PDF,
		label: 'PDF',
	},
	{
		value: STEP_INPUT_TYPE_MAP.ANY_FILE,
		label: 'Any File',
	},
];

const GroupFieldAllowedInputTypes = [
	STEP_INPUT_TYPE_MAP.DATE_TIME,
	STEP_INPUT_TYPE_MAP.DATE_ONLY,
	STEP_INPUT_TYPE_MAP.TIME_ONLY,
	STEP_INPUT_TYPE_MAP.TIME_RANGE,
	STEP_INPUT_TYPE_MAP.TEXT,
	STEP_INPUT_TYPE_MAP.MULTIPLE_CHOICE,
	STEP_INPUT_TYPE_MAP.NUMBER,
];

export const GroupFieldInputTypes = StepInputTypes.filter((it) =>
	GroupFieldAllowedInputTypes.includes(it.value)
);

export const getStepInputLabel = (val) =>
	nullSafeGetOrElse(
		'label',
		StepInputTypes.find((_) => _.value === val),
		val
	);

export const isAttachmentInputType = (step) =>
	[STEP_INPUT_TYPE_MAP.IMAGE, STEP_INPUT_TYPE_MAP.PDF, STEP_INPUT_TYPE_MAP.ANY_FILE].includes(
		step.inputType
	);

export const updateTroubleshootingAnswer = (step, answer) =>
	isAttachmentInputType(step)
		? { attachments: answer }
		: step.allowMultipleAnswerSelections && Array.isArray(answer)
		? { multipleAnswers: answer }
		: { answer };

const getTroubleshootingBackendInput = (tsAnswer) => {
	const step = nullSafeGetOrElse('troubleshootingStep', tsAnswer, {});

	const ans = nullSafeGet('answer', tsAnswer);

	if (!ans) {
		return undefined;
	}

	return formatValueForBackendByInputType(step.inputType, ans);
};

const getTroubleshootingBackendAttachments = (tsAnswer) => {
	const attachments = nullSafeGetOrElse('attachments', tsAnswer, []);

	return isAttachmentInputType(tsAnswer.troubleshootingStep)
		? attachments.map((att) => {
				const fileName = nullSafeGetOrElse('response.data.fileName', att, '');
				const fileId = nullSafeGetOrElse('response.data.fileId', att, '');
				return !!fileId && !!fileName ? { fileId, fileName } : undefined;
		  })
		: undefined;
};

const formatValueForBackendByInputType = (inputType, ans) => {
	switch (inputType) {
		case STEP_INPUT_TYPE_MAP.DATE_ONLY:
		case STEP_INPUT_TYPE_MAP.DATE_TIME:
		case STEP_INPUT_TYPE_MAP.TIME_ONLY:
			return ans && ans.utc ? ans.utc().format() : ans;
		case STEP_INPUT_TYPE_MAP.TIME_RANGE:
			return Array.isArray(ans) ? ans.map((_) => (_.utc ? _.utc().format() : _)).join(',') : ans;
		case STEP_INPUT_TYPE_MAP.NUMBER:
			return ans !== undefined && ans !== null ? `${ans}` : ans;
		case STEP_INPUT_TYPE_MAP.IMAGE:
		case STEP_INPUT_TYPE_MAP.PDF:
		case STEP_INPUT_TYPE_MAP.ANY_FILE:
			return undefined;
		case STEP_INPUT_TYPE_MAP.MULTIPLE_CHOICE:
			return nullSafeGet('allowMultipleAnswerSelections', ans) ? undefined : ans;
		default:
			return ans;
	}
};

const buildGroupFieldAnswers = (fields, answer) => {
	const buildSingleGroupAnswerArray = (groupAns) =>
		fields
			.map((field, idx) => {
				const key = nullSafeGetOrElse('name', field, `field_${idx}`);
				const fieldInputType = nullSafeGetOrElse('inputType', field, STEP_INPUT_TYPE_MAP.TEXT);
				const rawValue = groupAns ? groupAns[key] : undefined;
				const formattedValue = formatValueForBackendByInputType(fieldInputType, rawValue);
				if (formattedValue === undefined || formattedValue === null || formattedValue === '') {
					return undefined;
				}
				return {
					groupField: field,
					answer: formattedValue,
				};
			})
			.filter((_) => !!_);

	if (Array.isArray(answer)) {
		return answer.map((groupAns, idx) => ({
			answer: buildSingleGroupAnswerArray(groupAns || {}),
			orderingId: idx + 1,
		}));
	}

	return [
		{
			answer: buildSingleGroupAnswerArray(answer || {}),
			orderingId: 1,
		},
	];
};

export const getTroubleshootingBackendAnswers = (tsAnswer) => {
	const step = nullSafeGetOrElse('troubleshootingStep', tsAnswer, {});
	const inputType = nullSafeGet('inputType', step);

	if (
		inputType === STEP_INPUT_TYPE_MAP.GROUP ||
		inputType === STEP_INPUT_TYPE_MAP.REPEATABLE_GROUP
	) {
		const fields = nullSafeGetOrElse('groupFields', step, []);
		const ans = nullSafeGet('answer', tsAnswer);

		const groupFieldAnswers = buildGroupFieldAnswers(fields, ans);

		return {
			answer: undefined,
			attachments: undefined,
			multipleAnswers: undefined,
			groupFieldAnswers,
		};
	}

	return {
		answer: getTroubleshootingBackendInput(tsAnswer),
		attachments: getTroubleshootingBackendAttachments(tsAnswer),
		multipleAnswers: nullSafeGet('multipleAnswers', tsAnswer)
			? nullSafeGet('multipleAnswers', tsAnswer)
			: undefined,
	};
};

const formatValueByInputType = (inputType, value, multipleChoicesForField = []) => {
	if (value === undefined || value === null || value === '') {
		return '--';
	}

	switch (inputType) {
		case STEP_INPUT_TYPE_MAP.DATE_ONLY:
			return moment(value).format(DATE_ONLY_FORMAT);
		case STEP_INPUT_TYPE_MAP.DATE_TIME:
			return moment(value).format(DATE_FORMAT);
		case STEP_INPUT_TYPE_MAP.TIME_ONLY:
			return moment(value).format(TIME_ONLY_FORMAT);
		case STEP_INPUT_TYPE_MAP.TIME_RANGE:
			return Array.isArray(value)
				? `From  ${value.map((_) => moment(_).format(TIME_ONLY_FORMAT)).join(' to ')}`
				: typeof value === 'string'
				? `From  ${value
						.split(',')
						.map((_) => moment(_).format(TIME_ONLY_FORMAT))
						.join(' to ')}`
				: '--';
		case STEP_INPUT_TYPE_MAP.MULTIPLE_CHOICE: {
			const render = (mcAns) => {
				const answer = multipleChoicesForField.find((_) => _.value === mcAns);
				return !!answer
					? `${answer.value}${answer.description ? ` - ${answer.description}` : ''}`
					: mcAns;
			};
			return Array.isArray(value)
				? value.length > 0
					? value.map((_) => render(_)).join(', ')
					: '--'
				: render(value);
		}
		default:
			return value;
	}
};

const buildGroupAnswersFromItems = (items) =>
	(items || []).reduce((acc, item, idx) => {
		const key = nullSafeGet('groupField.name', item) || `field_${idx}`;
		const field = nullSafeGet('groupField', item) || {};
		const fieldInputType = nullSafeGetOrElse('inputType', field, STEP_INPUT_TYPE_MAP.TEXT);
		const raw = nullSafeGet('answer', item);
		const tsAnswerForField = {
			troubleshootingStep: {
				inputType: fieldInputType,
				allowMultipleAnswerSelections: nullSafeGet('allowMultipleAnswerSelections', field),
			},
			answer: Array.isArray(raw) ? undefined : raw,
			multipleAnswers: Array.isArray(raw) ? raw : undefined,
		};
		return {
			...acc,
			[key]: formatForTroubleshootingAnswerInput(tsAnswerForField),
		};
	}, {});

export const formatForTroubleshootingAnswerInput = (tsAnswer) => {
	const step = nullSafeGetOrElse('troubleshootingStep', tsAnswer, {});

	const ans = nullSafeGet('answer', tsAnswer);
	const multipleAnswers = nullSafeGetOrElse('multipleAnswers', tsAnswer, []);
	const attachments = nullSafeGetOrElse('attachments', tsAnswer, []);

	switch (step.inputType) {
		case STEP_INPUT_TYPE_MAP.DATE_TIME:
		case STEP_INPUT_TYPE_MAP.DATE_ONLY:
		case STEP_INPUT_TYPE_MAP.TIME_ONLY:
			return ans ? moment(ans) : undefined;
		case STEP_INPUT_TYPE_MAP.TIME_RANGE:
			return ans ? ans.split(',').map((_) => moment(_)) : undefined;
		case STEP_INPUT_TYPE_MAP.NUMBER:
			return ans ? parseFloat(ans) : undefined;
		case STEP_INPUT_TYPE_MAP.IMAGE:
		case STEP_INPUT_TYPE_MAP.PDF:
		case STEP_INPUT_TYPE_MAP.ANY_FILE:
			return getDefaultFileListForImages(attachments);
		case STEP_INPUT_TYPE_MAP.MULTIPLE_CHOICE:
			return step.allowMultipleAnswerSelections ? multipleAnswers : ans;
		// group and repeatable group are handled differently because
		// group -> form.item : flat input fields
		// repeatable group -> form.list : nested input fields
		case STEP_INPUT_TYPE_MAP.GROUP: {
			const groupFieldAnswers = nullSafeGet('groupFieldAnswers', tsAnswer);
			const items = nullSafeGet('0.answer', groupFieldAnswers) || [];
			return buildGroupAnswersFromItems(items);
		}
		case STEP_INPUT_TYPE_MAP.REPEATABLE_GROUP: {
			const groupFieldAnswers = nullSafeGet('groupFieldAnswers', tsAnswer);
			return groupFieldAnswers.map((grp) =>
				buildGroupAnswersFromItems(nullSafeGet('answer', grp) || [])
			);
		}
		default:
			return ans;
	}
};

const formatGroupFieldAnswers = (groupFieldAnswers) => {
	if (!groupFieldAnswers || groupFieldAnswers.length === 0) return [];
	return groupFieldAnswers.map((grp) => {
		const items = nullSafeGetOrElse('answer', grp, []);
		return items
			.map((item) => {
				const label = nullSafeGetOrElse('groupField.label', item, '');
				const value = formatValueByInputType(
					nullSafeGetOrElse('groupField.inputType', item, STEP_INPUT_TYPE_MAP.TEXT),
					nullSafeGet('answer', item)
				);
				return label && value ? { label, value } : undefined;
			})
			.filter((_) => !!_);
	});
};

export const getTroubleshootingAnswerFormatted = (tsAnswer) => {
	const step = nullSafeGetOrElse('troubleshootingStep', tsAnswer, STEP_INPUT_TYPE_MAP.TEXT);

	const multipleChoices = nullSafeGetOrElse('troubleshootingStep.multipleChoices', tsAnswer, []);
	const ans = nullSafeGet('answer', tsAnswer);
	const multipleAnswers = nullSafeGet('multipleAnswers', tsAnswer);
	const attachments = nullSafeGetOrElse('attachments', tsAnswer, '');
	const groupFieldAnswers = nullSafeGetOrElse('groupFieldAnswers', tsAnswer, []);

	switch (step.inputType) {
		case STEP_INPUT_TYPE_MAP.DATE_ONLY:
			return formatValueByInputType(STEP_INPUT_TYPE_MAP.DATE_ONLY, ans);
		case STEP_INPUT_TYPE_MAP.DATE_TIME:
			return formatValueByInputType(STEP_INPUT_TYPE_MAP.DATE_TIME, ans);
		case STEP_INPUT_TYPE_MAP.TIME_ONLY:
			return formatValueByInputType(STEP_INPUT_TYPE_MAP.TIME_ONLY, ans);
		case STEP_INPUT_TYPE_MAP.TIME_RANGE:
			return formatValueByInputType(STEP_INPUT_TYPE_MAP.TIME_RANGE, ans);
		case STEP_INPUT_TYPE_MAP.MULTIPLE_CHOICE:
			const renderMCAns = (mcAns) => {
				const answer = multipleChoices.find((_) => _.value === mcAns);
				return !!answer
					? `${answer.value} ${answer.description ? ` - ${answer.description}` : ''}`
					: mcAns;
			};
			if (step.allowMultipleAnswerSelections) {
				return nullSafeGet('0', multipleAnswers)
					? {
							type: step.inputType,
							value: (multipleAnswers || []).map((_) => renderMCAns(_)),
					  }
					: '--';
			} else {
				return !ans ? '--' : renderMCAns(ans);
			}
		case STEP_INPUT_TYPE_MAP.IMAGE:
			return {
				type: step.inputType,
				value: attachments.map((p) => `${p.fileId}/${p.fileName}`),
			};
		case STEP_INPUT_TYPE_MAP.PDF:
		case STEP_INPUT_TYPE_MAP.ANY_FILE:
			return {
				type: step.inputType,
				value: attachments,
			};
		case STEP_INPUT_TYPE_MAP.GROUP: {
			const formatted = formatGroupFieldAnswers([groupFieldAnswers[0]]);
			return { type: STEP_INPUT_TYPE_MAP.GROUP, value: formatted[0] || [] };
		}
		case STEP_INPUT_TYPE_MAP.REPEATABLE_GROUP: {
			const value = formatGroupFieldAnswers(groupFieldAnswers);
			return { type: STEP_INPUT_TYPE_MAP.REPEATABLE_GROUP, value };
		}
		default:
			return ans;
	}
};

export const subAnswerReduceFunction = (ans) => ({
	answer: ans.answer,
	attachments: ans.attachments,
	multipleAnswers: ans.multipleAnswers,
	troubleshootingStep: ans.troubleshootingStep,
	groupFieldAnswers: ans.groupFieldAnswers,
});

const getTroubleshootingAnswersForWOStep = (stepName, tsAttributes, tsAnswers) => {
	const tsAnswer = tsAnswers.find((tsAns) => {
		const tsAttr = tsAttributes.find((tsA) => tsA.id === tsAns.troubleshootingAttributeId);
		return nullSafeGet('previousStep', tsAttr) === stepName;
	});
	const allAnswersIncludingSubStepAnswers = flattenTreeData(
		nullSafeGetOrElse('troubleshootingAnswers', tsAnswer, []),
		subAnswerReduceFunction,
		'subStepAnswers'
	);
	const answerSteps = allAnswersIncludingSubStepAnswers.map((_) => ({
		key: nullSafeGetOrElse('troubleshootingStep.title', _, ''),
		value: getTroubleshootingAnswerFormatted(_),
	}));
	return answerSteps.length > 0 ? answerSteps : undefined;
};

export const getTroubleshootingAnswersForNecessaryWOSteps = (state) => {
	const tsRoutes = nullSafeGetOrElse('troubleshootingRoutes', state, []);
	const tsAnswers = nullSafeGetOrElse('troubleshootingAnswers', state, []);
	const locationTsAnswer =
		getTroubleshootingAnswersForWOStep(WO_STEP_NAMES.LOCATIONS, tsRoutes, tsAnswers) ||
		getTroubleshootingAnswersForWOStep(WO_STEP_NAMES.ASSET, tsRoutes, tsAnswers) ||
		[];
	const problemTypeTsAnswer =
		getTroubleshootingAnswersForWOStep(WO_STEP_NAMES.PROBLEM_TYPE_TOP_LEVEL, tsRoutes, tsAnswers) ||
		getTroubleshootingAnswersForWOStep(
			WO_STEP_NAMES.PROBLEM_TYPE_NEXT_LEVEL,
			tsRoutes,
			tsAnswers
		) ||
		getTroubleshootingAnswersForWOStep(
			WO_STEP_NAMES.PROBLEM_TYPE_NEXT_BRANCH,
			tsRoutes,
			tsAnswers
		) ||
		[];
	const priorityTsAnswer =
		getTroubleshootingAnswersForWOStep(WO_STEP_NAMES.PRIORITY, tsRoutes, tsAnswers) || [];

	return {
		locationTsAnswer,
		problemTypeTsAnswer,
		priorityTsAnswer,
	};
};

export const getExistingTsAnswersFromStateForWOTsRoute = (state, route) => {
	const currentTsAnswer = nullSafeGetOrElse('troubleshootingAnswers', state, []).find(
		(_) => _.troubleshootingAttributeId === route.id
	);
	return nullSafeGetOrElse('troubleshootingAnswers', currentTsAnswer, []);
};

export const getUpdatedTsAnswersState = (state, route, answers) => {
	const currentTsAnswer = nullSafeGetOrElse('troubleshootingAnswers', state, []).find(
		(_) => _.troubleshootingAttributeId === route.id
	);
	const updatedTsAnswer = {
		troubleshootingAttributeId: route.id,
		troubleshootingAnswers: answers,
	};
	const existingAnswers = nullSafeGetOrElse('troubleshootingAnswers', state, []);
	return !!currentTsAnswer
		? existingAnswers.map((_) => (_.troubleshootingAttributeId === route.id ? updatedTsAnswer : _))
		: [...existingAnswers, ...[updatedTsAnswer]];
};

export const getUpdatedTroubleshootingStates = (routeHierarchy, currentRoute, state) => {
	const excludeRoutesIndex = routeHierarchy.indexOf(currentRoute);
	const excludeRoutes = excludeRoutesIndex >= 0 ? routeHierarchy.slice(excludeRoutesIndex) : [];
	const existingTroubleshootingRoutes = nullSafeGetOrElse('troubleshootingRoutes', state, []);
	const troubleshootingRoutes = existingTroubleshootingRoutes.filter(
		(_) => !excludeRoutes.includes(_.previousStep)
	);
	const existingTroubleshootingAnswers = nullSafeGetOrElse('troubleshootingAnswers', state, []);
	const troubleshootingAnswers = existingTroubleshootingAnswers.filter(
		(_) => !!troubleshootingRoutes.find((r) => r.id === _.troubleshootingAttributeId)
	);

	return {
		troubleshootingRoutes,
		troubleshootingAnswers,
	};
};

export const getSubstepsIfAny = (step, answer) => {
	if (step.inputType === STEP_INPUT_TYPE_MAP.MULTIPLE_CHOICE) {
		const revisedAnswer = Array.isArray(answer) ? (answer.length === 1 ? answer[0] : null) : answer;
		const selectedChoice = revisedAnswer
			? nullSafeGetOrElse('multipleChoices', step, []).find((_) => _.value === revisedAnswer)
			: null;
		return nullSafeGet('subSteps', selectedChoice);
	} else {
		return null;
	}
};

export const isAssociationsNotSpecified = (record) =>
	nullSafeGetOrElse('problemTypeIds', record, []).length === 0 &&
	nullSafeGetOrElse('locationIds', record, []).length === 0 &&
	nullSafeGetOrElse('workOrderPriorityIds', record, []).length === 0;

export const areStepsAvailable = (key, guide) => nullSafeGetOrElse(key, guide, []).length > 0;
